import { timetable } from '../data/academics'
import {
  EVIDENCE_WEIGHT,
  EVIDENCE_WINDOW_MINUTES,
  FRESHNESS_THRESHOLDS,
  RELIABILITY_MINIMUM,
  powerLocationById,
  powerLocations,
} from '../data/power'
import type {
  NextClassContext,
  PowerConfidence,
  PowerFreshness,
  PowerLocation,
  PowerNearbyEntry,
  PowerReliability,
  PowerReport,
  PowerSourceKind,
  PowerState,
  PowerStatus,
} from '../types/power'

const MINUTE = 60_000

export function minutesSince(iso: string, now = Date.now()): number {
  return Math.max(0, Math.round((now - new Date(iso).getTime()) / MINUTE))
}

/** Freshness from configured thresholds, never from a number in a component. */
export function freshnessOf(iso: string | null, now = Date.now()): PowerFreshness {
  if (!iso) return 'none'
  const age = minutesSince(iso, now)
  for (const threshold of FRESHNESS_THRESHOLDS) {
    if (age <= threshold.withinMinutes) return threshold.level
  }
  return 'stale'
}

export function freshnessLabel(iso: string | null, now = Date.now()): string {
  if (!iso) return 'No recent confirmation'
  const age = minutesSince(iso, now)
  if (age < 1) return 'Confirmed just now'
  if (age < 60) return `${age} min ago`
  const hours = Math.round(age / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export function confirmedLabel(iso: string | null, now = Date.now()): string {
  if (!iso) return 'No recent confirmation'
  const level = freshnessOf(iso, now)
  const prefix = level === 'fresh' ? 'Confirmed' : 'Last confirmed'
  return `${prefix} ${freshnessLabel(iso, now)}`
}

export const CONFIDENCE_LABEL: Record<PowerConfidence, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
  none: 'No recent data',
}

/**
 * The aggregation layer. The newest report is deliberately NOT the answer: each
 * piece of evidence inside the window is weighted by its source and decayed by
 * age, the states are compared, and the winning state carries the agreement
 * behind it. Genuine disagreement lowers confidence rather than being hidden.
 */
export function deriveStatus(
  locationId: string,
  reports: PowerReport[],
  now = Date.now(),
): PowerStatus {
  const relevant = reports
    .filter(
      (item) =>
        item.locationId === locationId &&
        minutesSince(item.reportedAt, now) <= EVIDENCE_WINDOW_MINUTES,
    )
    .sort((a, b) => b.reportedAt.localeCompare(a.reportedAt))

  const base: PowerStatus = {
    locationId,
    state: 'unknown',
    confidence: 'none',
    source: 'unknown',
    lastConfirmedAt: null,
    reportCount: 0,
    evidence: null,
    freshness: 'none',
    agreement: 0,
    conflicting: false,
    calculatedAt: new Date(now).toISOString(),
  }
  if (relevant.length === 0) return base

  const weights = new Map<PowerState, number>()
  let total = 0
  for (const item of relevant) {
    const age = minutesSince(item.reportedAt, now)
    // Linear decay across the window: evidence at the edge counts for a tenth.
    const decay = Math.max(0.1, 1 - age / EVIDENCE_WINDOW_MINUTES)
    const weight = EVIDENCE_WEIGHT[item.evidence] * decay
    weights.set(item.state, (weights.get(item.state) ?? 0) + weight)
    total += weight
  }

  let state: PowerState = 'unknown'
  let best = 0
  for (const [candidate, weight] of weights) {
    if (weight > best) {
      best = weight
      state = candidate
    }
  }

  const agreement = total > 0 ? best / total : 0
  const agreeing = relevant.filter((item) => item.state === state)
  const newest = agreeing[0] ?? relevant[0]
  const conflicting = weights.size > 1 && agreement < 0.75

  return {
    ...base,
    state,
    source: resolveSource(agreeing),
    lastConfirmedAt: newest.reportedAt,
    reportCount: relevant.length,
    evidence: newest.evidence,
    freshness: freshnessOf(newest.reportedAt, now),
    agreement,
    conflicting,
    confidence: deriveConfidence(best, agreement, newest.reportedAt, now),
  }
}

function resolveSource(reports: PowerReport[]): PowerSourceKind {
  const stated = reports.filter((item) => item.source !== 'unknown')
  if (stated.length === 0) return 'unknown'
  const counts = new Map<PowerSourceKind, number>()
  for (const item of stated) counts.set(item.source, (counts.get(item.source) ?? 0) + 1)
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]
}

function deriveConfidence(
  weight: number,
  agreement: number,
  newestAt: string,
  now: number,
): PowerConfidence {
  const freshness = freshnessOf(newestAt, now)
  if (freshness === 'stale') return 'low'
  if (weight >= 3 && agreement >= 0.8 && freshness === 'fresh') return 'high'
  if (weight >= 1.6 && agreement >= 0.6) return 'medium'
  return 'low'
}

/** Every source stated on one status, for the detail card. */
export function sourcesFor(
  locationId: string,
  reports: PowerReport[],
  now = Date.now(),
): PowerSourceKind[] {
  const seen = new Set<PowerSourceKind>()
  for (const item of reports) {
    if (item.locationId !== locationId) continue
    if (minutesSince(item.reportedAt, now) > EVIDENCE_WINDOW_MINUTES) continue
    if (item.source !== 'unknown') seen.add(item.source)
  }
  return [...seen]
}

/** Straight-line distance in km. Used for "near me", never for travel time. */
export function distanceKm(a: PowerLocation, b: PowerLocation): number {
  const toRad = (value: number) => (value * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.asin(Math.sqrt(h))
}

/**
 * Nearby statuses. A personal location is only ever included when it is the
 * viewer's own reference point — one tenancy is never surfaced to anybody else.
 */
export function nearbyStatuses(
  fromId: string,
  reports: PowerReport[],
  radiusKm = 1,
  now = Date.now(),
): PowerNearbyEntry[] {
  const origin = powerLocationById(fromId)
  if (!origin) return []
  return powerLocations
    .filter((location) => location.id !== fromId)
    .filter((location) => !location.personal)
    .map((location) => ({
      location,
      status: deriveStatus(location.id, reports, now),
      distanceKm: distanceKm(origin, location),
    }))
    .filter((entry) => entry.distanceKm <= radiusKm)
    .sort((a, b) => a.distanceKm - b.distanceKm)
}

/**
 * Reliability. Refuses to produce a percentage until there is enough history to
 * mean something — an uptime figure from four reports would be a fabrication with
 * a decimal point on it.
 */
export function deriveReliability(
  locationId: string,
  reports: PowerReport[],
  now = Date.now(),
): PowerReliability {
  const forLocation = reports.filter((item) => item.locationId === locationId)
  const days = new Set(forLocation.map((item) => item.reportedAt.slice(0, 10)))
  if (
    forLocation.length < RELIABILITY_MINIMUM.reports ||
    days.size < RELIABILITY_MINIMUM.days
  ) {
    return { locationId, sufficient: false, reportCount: forLocation.length }
  }
  const powered = forLocation.filter(
    (item) => item.state === 'on' || item.state === 'backup',
  ).length
  return {
    locationId,
    sufficient: true,
    reportCount: forLocation.length,
    uptimeShare: powered / forLocation.length,
    sampleDays: days.size,
  }
}

const DAY_ORDER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const DAY_FULL: Record<string, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
}

function toMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * The next timetabled class, computed from the real timetable and the real clock.
 * Nothing is invented: with no remaining session this returns null, and the
 * interface says so rather than showing a countdown to nothing.
 */
export function nextClass(now = new Date()): NextClassContext | null {
  const today = DAY_ORDER[now.getDay()]
  const minutesNow = now.getHours() * 60 + now.getMinutes()

  const laterToday = timetable
    .filter((session) => session.day === today && toMinutes(session.start) > minutesNow)
    .sort((a, b) => toMinutes(a.start) - toMinutes(b.start))[0]

  if (laterToday) {
    return {
      courseCode: laterToday.courseCode,
      title: laterToday.title,
      venue: laterToday.venue,
      startsInMinutes: toMinutes(laterToday.start) - minutesNow,
      locationId: matchVenue(laterToday.venue),
      laterInWeek: false,
      day: DAY_FULL[laterToday.day] ?? laterToday.day,
      start: laterToday.start,
    }
  }

  // Walk forward through the week for the next scheduled session.
  for (let offset = 1; offset <= 7; offset += 1) {
    const day = DAY_ORDER[(now.getDay() + offset) % 7]
    const session = timetable
      .filter((item) => item.day === day)
      .sort((a, b) => toMinutes(a.start) - toMinutes(b.start))[0]
    if (session) {
      return {
        courseCode: session.courseCode,
        title: session.title,
        venue: session.venue,
        startsInMinutes: offset * 24 * 60 + toMinutes(session.start) - minutesNow,
        locationId: matchVenue(session.venue),
        laterInWeek: true,
        day: DAY_FULL[session.day] ?? session.day,
        start: session.start,
      }
    }
  }
  return null
}

/** Maps a freeform timetable venue onto a known Power location, or nothing. */
export function matchVenue(venue: string): string | null {
  const needle = venue.toLowerCase()
  for (const location of powerLocations) {
    if (location.name.toLowerCase() === needle) return location.id
    for (const keyword of location.venueKeywords ?? []) {
      if (needle.includes(keyword)) return location.id
    }
  }
  return null
}

export function countdownLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours < 24) return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`
  return `${Math.round(hours / 24)} day${hours >= 48 ? 's' : ''}`
}

/**
 * The plain-language verdict for Before you go. It states the weakest thing it
 * found rather than averaging into a reassuring middle.
 */
export function journeyVerdict(statuses: PowerStatus[]): {
  tone: 'ok' | 'warn' | 'neutral'
  message: string
} {
  if (statuses.length === 0 || statuses.every((status) => status.state === 'unknown')) {
    return { tone: 'neutral', message: 'Not enough recent reports to say.' }
  }
  const out = statuses.filter((status) => status.state === 'off')
  const shaky = statuses.filter(
    (status) => status.state === 'unstable' || status.state === 'backup',
  )
  const unknown = statuses.filter((status) => status.state === 'unknown')
  if (out.length > 0) {
    return {
      tone: 'warn',
      message:
        out.length === 1
          ? 'Power is out at one of your places.'
          : `Power is out at ${out.length} of your places.`,
    }
  }
  if (shaky.length > 0) return { tone: 'warn', message: 'One place is running on backup.' }
  if (unknown.length > 0) {
    return { tone: 'neutral', message: 'Some places have no recent reports.' }
  }
  return { tone: 'ok', message: 'Everything looks good.' }
}
